import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { Brand, Meeting, Note } from "../types/brandDatas"
import "./styles/styles.css"
import {
  TAbrandSalesReadiness,
  TAbrandSize,
  TAgetBrandCampaigns,
  TAupdateBalance,
  TAupdateBrand,
  TAupdateBrandLogo,
  TAupdateBrandMeeting,
  TAdeleteBrandMeeting,
  TAupdateNote,
  TAdeleteNote,
  TAbrandSetCpmAim,
  TAbrandSetLeadStyle,
  TAbrandSetUtm,
  TAredirectBrandAccount,
} from "../services/brandAPI"
import { selectUser } from "../redux/store/auth/authSlice"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTrash } from "@fortawesome/free-solid-svg-icons"
import CampaignCard from "./CampaignCard"
import UpdateBrandBalance from "./UpdateBrandBalance"
import NotesModal from "../pages/Brand/allBrands/components/NotesModal"
import MeetingModal from "../pages/Brand/allBrands/components/MeetingModal"
import UtmModal from "../pages/Brand/allBrands/components/UtmModal"
import { useNavigate } from "react-router-dom"
import { getErrorStatus } from "../types/error"

const BrandProfile = (brand: Brand) => {
  const navigate = useNavigate()
  const userEmail = useSelector(selectUser)?.replace(/"/g, "") || ""
  const [brand_logo, setBrandLogo] = useState(brand.brand_logo)
  const [balance, setBalance] = useState(brand.balance)
  const [newBalance, setNewBalance] = useState(brand.balance)
  const [notes, setNotes] = useState<Note[]>((brand.notes as Note[]) ?? [])
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false)
  const [isMeetingModalOpen, setIsMeetingModalOpen] = useState(false)
  const [meetings, setMeetings] = useState<Meeting[]>(brand.meetings ?? [])
  const [editBalance, setEditBalance] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [logo_url, setLogo_url] = useState("")
  const [brandCampaigns, setBrandCampaigns] = useState([])
  const [operation, setOperation] = useState("")
  const [rate, setRate] = useState(brand.customer_success_input?.sales_readiness || 0)
  const [editor, setEditor] = useState(false)
  const [editRate, setEditRate] = useState(rate)
  const [brandSize, setBrandSize] = useState(brand.brand_size || "small")
  const [editBrandSize, setEditBrandSize] = useState(false)
  const [selectedSize, setSelectedSize] = useState(brand.brand_size || "small")
  const [cpmAim, setCpmAim] = useState(brand.cpm_aim || 0)
  const [editCpmAim, setEditCpmAim] = useState(false)
  const [editCpmAimValue, setEditCpmAimValue] = useState(brand.cpm_aim || 0)
  const [leadStyle, setLeadStyle] = useState(brand.lead_style || "")
  const [editLeadStyle, setEditLeadStyle] = useState(false)
  const [selectedLeadStyle, setSelectedLeadStyle] = useState(brand.lead_style || "")
  const [utm, setUtm] = useState(brand.utm || { utm_source: "", utm_medium: "", utm_campaign: "" })
  const [isUtmModalOpen, setIsUtmModalOpen] = useState(false)

  useEffect(() => {
    setBrandLogo(brand.brand_logo)
    setBalance(brand.balance)
    setNewBalance(brand.balance)
    setNotes((brand.notes as Note[]) ?? [])
    setMeetings(brand.meetings ?? [])
    const initialRate = brand.customer_success_input?.sales_readiness || 0
    setRate(initialRate)
    setEditRate(initialRate)
    setBrandSize(brand.brand_size || "small")
    setSelectedSize(brand.brand_size || "small")
    setCpmAim(brand.cpm_aim || 0)
    setEditCpmAimValue(brand.cpm_aim || 0)
    setLeadStyle(brand.lead_style || "")
    setSelectedLeadStyle(brand.lead_style || "")
    setUtm(brand.utm || { utm_source: "", utm_medium: "", utm_campaign: "" })

    // Reset UI states
    setIsNotesModalOpen(false)
    setIsMeetingModalOpen(false)
    setEditBalance(false)
    setIsOpen(false)
    setEditor(false)
    setEditBrandSize(false)
    setEditCpmAim(false)
    setEditLeadStyle(false)
    setIsUtmModalOpen(false)

    setBrandCampaigns([])
    handleCampaigns()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand])

  const getSizeLabel = (size: string) => {
    switch (size) {
      case "small":
        return "Küçük"
      case "medium":
        return "Orta"
      case "large":
        return "Büyük"
      default:
        return "Küçük"
    }
  }

  const getLeadStyleLabel = (style: string) => {
    switch (style) {
      case "dominance":
        return "Dominant"
      case "influencing":
        return "Etkileyici"
      case "cautious":
        return "Dikkatli"
      case "stabilizing":
        return "Stabilize"
      default:
        return "Belirtilmemiş"
    }
  }

  const brandInfo: { key: string; value: string | number | "small" | "medium" | "large" }[] = [
    { key: "Marka İsmi", value: brand.brand_name },
    { key: "Email", value: brand.email },
    { key: "Telefon", value: brand.phone },
    { key: "Ülke", value: brand.country },
    { key: "İsim", value: brand.first_name },
    { key: "Soyisim", value: brand.last_name },
    { key: "Para Birimi", value: brand.currency || "TRY" },
    { key: "Dil", value: brand.language || "TR" },
    { key: "Unvan", value: brand.job_title },
    { key: "Marka Boyutu", value: getSizeLabel(brandSize) },
    { key: "CPM Aim", value: cpmAim },
    { key: "Lead Style", value: getLeadStyleLabel(leadStyle || "") },
    { key: "Bakiye", value: balance },
    { key: "Satışa Yakınlık", value: rate },
    { key: "Referans", value: brand.reference || "" },
    { key: "Marka Id", value: brand._id },
  ]

  const handleCampaigns = async () => {
    try {
      const response = await TAgetBrandCampaigns(brand._id)
      if (response) {
        setBrandCampaigns(response)
      }
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      throw error
    }
  }

  const handleUpdateNote = async (brandId: string, note?: string, files?: FileList | File[]) => {
    try {
      const updatedBrand = await TAupdateNote(brandId, userEmail, note, files)
      setNotes(updatedBrand.notes || [])
      return (updatedBrand?.notes ?? []) as Note[]
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      return undefined
    }
  }

  const handleDeleteNote = async (index: number) => {
    try {
      const response = await TAdeleteNote(brand._id, index)
      if (response) {
        setNotes(response.notes || [])
      }
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      throw error
    }
  }

  const handleDeleteNoteFromModal = async (_brandId: string, index: number) => {
    await handleDeleteNote(index)
  }

  const handleAddMeeting = async (
    brandId: string,
    meeting: { time: Date; description: string },
  ) => {
    try {
      const response = await TAupdateBrandMeeting(brandId, meeting, userEmail)
      if (response) {
        setMeetings(response.meetings || [])
      }
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      throw error
    }
  }

  const handleDeleteMeeting = async (brandId: string, index: number) => {
    try {
      const response = await TAdeleteBrandMeeting(brandId, index)
      if (response) {
        setMeetings(response.meetings || [])
      }
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      throw error
    }
  }

  const handleUploadPhoto = async (logo_url: string) => {
    try {
      await TAupdateBrand(brand._id, { brand_logo: logo_url })
      setIsOpen(false)
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      throw error
    }
  }

  const handleSelectPhotoFromPC = async () => {
    try {
      const input = document.createElement("input")
      input.type = "file"
      input.accept = "image/*"
      input.onchange = async (e) => {
        if (!(e.target instanceof HTMLInputElement) || !e.target.files) {
          return
        }

        const file = e.target.files[0]
        const formData = new FormData()
        formData.append("brand_logo", file)

        const response = await TAupdateBrandLogo(brand._id, formData)
        if (response.status !== 200) {
          throw new Error("Failed to upload brand logo")
        }
        setIsOpen(false)
        setBrandLogo(response.data.brand.brand_logo)
      }
      input.click()
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      throw error
    }
  }

  const handleChangeBalance = async (newBalance: number, operation: string) => {
    try {
      const response = await TAupdateBalance(brand._id, newBalance, operation)
      if (response) {
        setEditBalance(false)
        setBalance(newBalance)
      }
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      throw error
    }
  }

  const handleSalesReadiness = async (_id: string, rate: number) => {
    try {
      const response = await TAbrandSalesReadiness(_id, rate)
      if (response) {
        setRate(rate)
      }
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      throw error
    }
  }

  const handleBrandSizeChange = async (_id: string, size: "small" | "medium" | "large") => {
    try {
      const response = await TAbrandSize(_id, size)
      if (response) {
        setBrandSize(size)
      }
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      throw error
    }
  }

  const handleCpmAimChange = async (_id: string, cpmAim: number) => {
    try {
      const response = await TAbrandSetCpmAim(_id, cpmAim)
      if (response) {
        setCpmAim(cpmAim)
      }
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      throw error
    }
  }

  const handleLeadStyleChange = async (
    _id: string,
    leadStyle: "dominance" | "influencing" | "cautious" | "stabilizing" | string,
  ) => {
    try {
      const response = await TAbrandSetLeadStyle(_id, leadStyle)
      if (response) {
        setLeadStyle(leadStyle)
      }
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      throw error
    }
  }

  const handleUtmChange = async (
    _id: string,
    utm: { utm_source?: string; utm_medium?: string; utm_campaign?: string },
  ) => {
    try {
      const response = await TAbrandSetUtm(_id, utm)
      if (response) {
        setUtm(utm)
      }
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      throw error
    }
  }

  const handleRedirectToBrandAccounts = async (id: string) => {
    try {
      const response = await TAredirectBrandAccount(id)
      if (response) {
        window.open(response.url, "_blank")
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Marka hesabına yönlendirme hatası:", error)
    }
  }

  return (
    <div>
      <div className="profile-section bg-white p-3 shadow-md mb-3 w-3/5 rounded-2xl">
        <h3 className="section-title text-xl font-bold mb-3">Marka Bilgileri</h3>
        {brand_logo && (
          <div className="flex items-center">
            <img src={brand_logo} alt="Brand Logo" className="w-20 h-20 rounded-full mr-4" />
            {!isOpen && (
              <button
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                onClick={() => setIsOpen(true)}
              >
                Resmi Değiştir
              </button>
            )}
          </div>
        )}
        {!brand_logo && (
          <div
            className="w-20 h-20 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 text-sm font-bold cursor-pointer"
            onClick={() => setIsOpen(true)}
          >
            Resim Ekle
          </div>
        )}
        {isOpen && (
          <div>
            <input
              type="text"
              placeholder="photo url'i girin"
              value={logo_url}
              onChange={(e) => setLogo_url(e.target.value)}
              className="border border-gray-400 rounded py-2 px-4 mb-2 mt-2"
            />
            <button
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 ml-2 rounded"
              onClick={() => handleUploadPhoto(logo_url)}
            >
              Kaydet
            </button>
            <button
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 ml-2 rounded"
              onClick={() => handleSelectPhotoFromPC()}
            >
              Bilgisayardan Yükle
            </button>
          </div>
        )}

        <table className="table-responsive">
          <tbody>
            <tr>
              <td className="py-2 text-gray-600">
                <button
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded text-sm inline-block"
                  onClick={() => handleRedirectToBrandAccounts(brand._id)}
                >
                  Marka Hesabına Git
                </button>
              </td>
            </tr>
            {brandInfo.map((info, index) => (
              <tr key={index}>
                {info.key === "Bakiye" ? (
                  <>
                    <td className="font-bold text-md">Bakiye</td>
                    <td className="font-semibold">=</td>
                    <td className="font-semibold">{info.value}</td>
                    <td>
                      <button
                        className="text-indigo-600 hover:text-indigo-900 pt-3"
                        onClick={() => setEditBalance(!editBalance)}
                      >
                        Bakiyeyi güncelle
                      </button>
                    </td>
                  </>
                ) : info.key === "Satışa Yakınlık" ? (
                  <>
                    <td className="font-bold text-md">Satışa Yakınlık</td>
                    <td className="font-semibold">=</td>
                    <td className="font-semibold">{info.value}</td>
                    <td>
                      <button
                        className="text-indigo-600 hover:text-indigo-900 pt-3"
                        onClick={() => setEditor(!editor)}
                      >
                        Oranı güncelle
                      </button>
                      {editor && (
                        <div className="flex flex-col space-y-2 pb-2">
                          <div className="flex flex-row space-x-1">
                            <input
                              id="phone"
                              type="tel"
                              placeholder="0-100"
                              value={editRate}
                              className="form-input text-sm border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 w-60 pt-1 pb-1"
                              onChange={(e) => setEditRate(Number(e.target.value))}
                            />
                            <button
                              className="text-sm font-medium text-indigo-600 hover:text-indigo-900"
                              onClick={() => {
                                handleSalesReadiness(brand._id, editRate)
                                setEditor(!editor)
                              }}
                            >
                              Güncelle
                            </button>
                          </div>
                        </div>
                      )}
                    </td>
                  </>
                ) : info.key === "Marka Boyutu" ? (
                  <>
                    <td className="font-bold text-md">Marka Boyutu</td>
                    <td className="font-semibold">=</td>
                    <td className="font-semibold">{info.value}</td>
                    <td>
                      <button
                        className="text-indigo-600 hover:text-indigo-900 pt-3"
                        onClick={() => setEditBrandSize(!editBrandSize)}
                      >
                        Boyutu güncelle
                      </button>
                      {editBrandSize && (
                        <div className="flex flex-col space-y-2 pb-2">
                          <div className="flex flex-row space-x-1">
                            <select
                              value={selectedSize}
                              onChange={(e) =>
                                setSelectedSize(e.target.value as "small" | "medium" | "large")
                              }
                              className="form-input text-sm border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 w-60 pt-1 pb-1"
                            >
                              <option value="small">Küçük</option>
                              <option value="medium">Orta</option>
                              <option value="large">Büyük</option>
                            </select>
                            <button
                              className="text-sm font-medium text-indigo-600 hover:text-indigo-900"
                              onClick={() => {
                                handleBrandSizeChange(brand._id, selectedSize)
                                setEditBrandSize(!editBrandSize)
                              }}
                            >
                              Güncelle
                            </button>
                          </div>
                        </div>
                      )}
                    </td>
                  </>
                ) : info.key === "CPM Aim" ? (
                  <>
                    <td className="font-bold text-md">CPM Aim</td>
                    <td className="font-semibold">=</td>
                    <td className="font-semibold">{info.value}</td>
                    <td>
                      <button
                        className="text-indigo-600 hover:text-indigo-900 pt-3"
                        onClick={() => setEditCpmAim(!editCpmAim)}
                      >
                        CPM Aim güncelle
                      </button>
                      {editCpmAim && (
                        <div className="flex flex-col space-y-2 pb-2">
                          <div className="flex flex-row space-x-1">
                            <input
                              type="number"
                              placeholder="CPM Aim"
                              value={editCpmAimValue}
                              className="form-input text-sm border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 w-60 pt-1 pb-1"
                              onChange={(e) => setEditCpmAimValue(Number(e.target.value))}
                            />
                            <button
                              className="text-sm font-medium text-indigo-600 hover:text-indigo-900"
                              onClick={() => {
                                handleCpmAimChange(brand._id, editCpmAimValue)
                                setEditCpmAim(!editCpmAim)
                              }}
                            >
                              Güncelle
                            </button>
                          </div>
                        </div>
                      )}
                    </td>
                  </>
                ) : info.key === "Lead Style" ? (
                  <>
                    <td className="font-bold text-md">Lead Style</td>
                    <td className="font-semibold">=</td>
                    <td className="font-semibold">{info.value}</td>
                    <td>
                      <button
                        className="text-indigo-600 hover:text-indigo-900 pt-3"
                        onClick={() => setEditLeadStyle(!editLeadStyle)}
                      >
                        Lead Style güncelle
                      </button>
                      {editLeadStyle && (
                        <div className="flex flex-col space-y-2 pb-2">
                          <div className="flex flex-row space-x-1">
                            <select
                              value={selectedLeadStyle}
                              onChange={(e) =>
                                setSelectedLeadStyle(
                                  e.target.value as
                                    | "dominance"
                                    | "influencing"
                                    | "cautious"
                                    | "stabilizing",
                                )
                              }
                              className="form-input text-sm border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 w-60 pt-1 pb-1"
                            >
                              <option value="dominance">Dominant</option>
                              <option value="influencing">Etkileyici</option>
                              <option value="cautious">Dikkatli</option>
                              <option value="stabilizing">Stabilize</option>
                            </select>
                            <button
                              className="text-sm font-medium text-indigo-600 hover:text-indigo-900"
                              onClick={() => {
                                handleLeadStyleChange(brand._id, selectedLeadStyle)
                                setEditLeadStyle(!editLeadStyle)
                              }}
                            >
                              Güncelle
                            </button>
                          </div>
                        </div>
                      )}
                    </td>
                  </>
                ) : (
                  <>
                    <td className="font-bold text-md">{info.key}</td>
                    <td className="font-semibold">=</td>
                    <td className="font-semibold">{info.value}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {/* Brand Notes */}
        <div className="flex flex-col mt-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-semibold text-gray-500">Notlar</label>
            <button
              className="px-3 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
              onClick={() => setIsNotesModalOpen(true)}
            >
              Notları Görüntüle
            </button>
          </div>

          {/* Kısa liste: eklerle birlikte önizleme */}
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200 mt-2">
            <ul>
              {notes
                .filter((n) => (n.note && n.note !== "") || (n.attachments?.length ?? 0) > 0)
                .map((note, index) => (
                  <li key={index} className="mb-4 p-3 bg-gray-50 rounded-lg">
                    {note.note && note.note !== "" && (
                      <p className="text-gray-800 font-medium mb-1">{note.note}</p>
                    )}
                    {note.attachments && note.attachments.length > 0 && (
                      <ul className="mt-1 space-y-1">
                        {note.attachments.map((att, i) => {
                          const label =
                            att.file_name ||
                            (att.mime_type?.includes("pdf")
                              ? "PDF"
                              : att.mime_type?.includes("word")
                              ? "DOCX"
                              : "Dosya")
                          return (
                            <li key={i} className="text-xs">
                              {att.file_url ? (
                                <a
                                  href={att.file_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-blue-600 hover:underline break-all"
                                >
                                  {label}
                                </a>
                              ) : (
                                <span className="text-gray-600">
                                  {label} <em className="text-gray-400">(yükleniyor…)</em>
                                </span>
                              )}
                              {att.file_size ? (
                                <span className="text-gray-400 ml-1">
                                  ({Math.round((att.file_size as number) / 1024)} KB)
                                </span>
                              ) : null}
                            </li>
                          )
                        })}
                      </ul>
                    )}

                    <div className="flex items-center mt-2 text-sm text-gray-500">
                      <span className="font-semibold">{note.author}</span>
                      <span className="mx-2">•</span>
                      <span>{new Date(note.note_time).toLocaleString("tr-TR")}</span>
                    </div>

                    <div className="flex justify-end">
                      <FontAwesomeIcon
                        icon={faTrash}
                        className="ml-3 mt-1"
                        style={{ color: "#005eff", cursor: "pointer" }}
                        onClick={() => handleDeleteNote(index)}
                      />
                    </div>
                  </li>
                ))}
              {notes.filter((n) => (n.note && n.note !== "") || (n.attachments?.length ?? 0) > 0)
                .length === 0 && <li className="text-gray-500">Henüz not eklenmemiş</li>}
            </ul>
          </div>
        </div>
        {/* Brand Meetings */}
        <div className="flex flex-col mt-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-semibold text-gray-500">Toplantılar</label>
            <button
              className="px-3 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
              onClick={() => setIsMeetingModalOpen(true)}
            >
              Toplantıları Görüntüle
            </button>
          </div>

          {/* Kısa liste: toplantılarla birlikte önizleme */}
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200 mt-2">
            <ul>
              {meetings && meetings.length > 0 ? (
                meetings.slice(0, 3).map((meeting, index) => (
                  <li key={index} className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center mb-2">
                      <p className="text-gray-800 font-medium text-sm">
                        {new Date(meeting.time).toLocaleString("tr-TR")}
                      </p>
                    </div>
                    <p className="text-gray-600 text-sm mb-2">{meeting.description}</p>
                    <div className="flex items-center text-xs text-gray-500">
                      <span className="font-semibold">Ekleyen:</span>
                      <span className="ml-1">{meeting.author}</span>
                    </div>
                  </li>
                ))
              ) : (
                <li className="text-gray-500">Henüz toplantı eklenmemiş</li>
              )}
              {meetings && meetings.length > 3 && (
                <li className="text-gray-500 text-sm">ve {meetings.length - 3} toplantı daha...</li>
              )}
            </ul>
          </div>
        </div>
        {/* UTM Parameters */}
        <div className="flex flex-col mt-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-semibold text-gray-500">UTM Parametreleri</label>
            <button
              className="px-3 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
              onClick={() => setIsUtmModalOpen(true)}
            >
              UTM Düzenle
            </button>
          </div>

          <div className="bg-white p-4 rounded-lg shadow border border-gray-200 mt-2">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-500">UTM Source</label>
                <p className="text-sm font-medium text-gray-800">
                  {utm.utm_source || "Belirtilmemiş"}
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">UTM Medium</label>
                <p className="text-sm font-medium text-gray-800">
                  {utm.utm_medium || "Belirtilmemiş"}
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">UTM Campaign</label>
                <p className="text-sm font-medium text-gray-800">
                  {utm.utm_campaign || "Belirtilmemiş"}
                </p>
              </div>
            </div>
          </div>
        </div>
        {brand?.billing_address && Object.values(brand.billing_address).some((value) => value) && (
          <div className="mt-5">
            <h3 className="section-title text-xl font-semibold mb-3">Ödeme Bilgileri</h3>
            <div className="flex flex-col space-y-2">
              {Object.entries(brand.billing_address).map(([key, value]) => (
                <div key={key} className="font-bold">
                  {key.charAt(0).toUpperCase() + key.slice(1)}:{" "}
                  <span className="font-semibold">{value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div>
        {brandCampaigns.length > 0 ? (
          <div>
            <h3 className="text-2xl font-semibold mb-4 text-gray-800 mt-10">
              Markaya Ait Kampanyalar
            </h3>
            <div className="overflow-x-auto py-4">
              <div className="flex space-x-4">
                {brandCampaigns.map((campaign, index) => (
                  <CampaignCard key={index} campaign={campaign} />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <p className="text-center text-gray-600">Markaya Ait Kampanya Bulunmamaktadır</p>
        )}
      </div>
      {brand?.money_exchanges && brand?.money_exchanges?.length > 0 && (
        <div>
          <h3 className="section-title text-xl font-semibold mt-10">İşlem Geçmişi</h3>
          <table className="table-auto mt-4">
            <thead>
              <tr>
                <th className="px-4 py-2 text-xs text-gray-500">Operasyon</th>
                <th className="px-4 py-2 text-xs text-gray-500">Miktar</th>
                <th className="px-4 py-2 text-xs text-gray-500">Başvuru ID</th>
                <th className="px-4 py-2 text-xs text-gray-500">İşlem Tarihi</th>
              </tr>
            </thead>
            <tbody>
              {brand.money_exchanges?.map((exchange, index) => (
                <tr key={index} className={index % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                  <td className="px-4 py-2 text-sm">{exchange.operation}</td>
                  <td className="px-4 py-2 text-sm">{exchange.amount}</td>
                  <td className="px-4 py-2 text-sm">{exchange.application_id}</td>
                  <td className="px-4 py-2 text-sm">
                    {new Date(exchange.action_time).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <UpdateBrandBalance
        modalOpen={editBalance}
        onClose={() => setEditBalance(false)}
        newBalance={newBalance}
        setNewBalance={setNewBalance}
        operation={operation}
        setOperation={setOperation}
        handleChangeBalance={handleChangeBalance}
      />
      <NotesModal
        isOpen={isNotesModalOpen}
        onClose={() => setIsNotesModalOpen(false)}
        brandId={brand._id}
        notes={notes}
        campaignNotes={brandCampaigns}
        onAddNote={(brandId, note, files) => handleUpdateNote(brandId, note, files)}
        onDeleteNote={handleDeleteNoteFromModal}
      />
      <MeetingModal
        isOpen={isMeetingModalOpen}
        onClose={() => setIsMeetingModalOpen(false)}
        brandId={brand._id}
        meetings={meetings}
        onAddMeeting={handleAddMeeting}
        onDeleteMeeting={handleDeleteMeeting}
      />
      <UtmModal
        isOpen={isUtmModalOpen}
        onClose={() => setIsUtmModalOpen(false)}
        brandId={brand._id}
        currentUtm={utm}
        onUpdateUtm={handleUtmChange}
      />
    </div>
  )
}

export default BrandProfile
